import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllReservations, updateReservationStatus, deleteReservation, getAllContacts, updateContactStatut, deleteContact, logoutAdmin, isAdminLoggedIn } from '../services/api';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import autoTable from 'jspdf-autotable';
import '../styles/AdminDashboard.css';

function AdminDashboard() {
  const [demandes, setDemandes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [filtreBesoin, setFiltreBesoin] = useState('tous');
  const [filtreContactStatut, setFiltreContactStatut] = useState('tous');
  const [activeSection, setActiveSection] = useState('demandes');
  const [selectedDemandes, setSelectedDemandes] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdminLoggedIn()) {
      navigate('/admin/login');
      return;
    }
    fetchDemandes();
    fetchContacts();
  }, [navigate]);

  const fetchDemandes = async () => {
    try {
      const response = await getAllReservations();
      setDemandes(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await getAllContacts();
      setContacts(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleStatusChange = async (id, statut) => {
    try {
      await updateReservationStatus(id, statut);
      fetchDemandes();
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer cette demande ?')) {
      try {
        await deleteReservation(id);
        fetchDemandes();
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  const handleContactStatusChange = async (id, statut) => {
    try {
      await updateContactStatut(id, statut);
      fetchContacts();
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleContactDelete = async (id) => {
    if (window.confirm('Supprimer ce message ?')) {
      try {
        await deleteContact(id);
        fetchContacts();
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  const handleLogout = () => {
    logoutAdmin();
    navigate('/admin/login');
  };

  // Sélection des demandes pour export
  const handleSelectDemande = (id) => {
    setSelectedDemandes(prev => {
      if (prev.includes(id)) {
        return prev.filter(d => d !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedDemandes.length === demandesFiltrees.length) {
      setSelectedDemandes([]);
    } else {
      setSelectedDemandes(demandesFiltrees.map(d => d._id));
    }
  };

  const exportToExcel = (exportAll = false) => {
  const dataToExport = exportAll
    ? demandesFiltrees
    : demandesFiltrees.filter(d => selectedDemandes.includes(d._id));

  if (dataToExport.length === 0) {
    alert('Veuillez sélectionner au moins une demande à exporter');
    return;
  }

  // Feuille 1 : liste des demandes
  const rows = dataToExport.map(d => ({
    'Date': new Date(d.createdAt).toLocaleDateString('fr-FR'),
    'Prénom': d.prenom,
    'Nom': d.nom,
    'Téléphone': d.telephone,
    'Email': d.email || '-',
    'Adresse': d.adresse || '-',
    'Démarche': besoinLabels[d.besoin] || d.besoin,
    'Créneau': d.creneauHoraire || '-',
    'Concerne': d.concernePour === 'moi-meme' ? 'Lui-même' : 'Quelqu\'un d\'autre',
    'Commentaire': d.commentaire || '-',
    'Statut': statutLabels[d.statut] || d.statut,
  }));

  const ws = XLSX.utils.json_to_sheet(rows);

  // Largeurs de colonnes
  ws['!cols'] = [
    { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 16 },
    { wch: 28 }, { wch: 30 }, { wch: 30 }, { wch: 12 },
    { wch: 18 }, { wch: 40 }, { wch: 12 },
  ];

  // Feuille 2 : statistiques
  const enAttente = dataToExport.filter(d => d.statut === 'en_attente').length;
  const contacte  = dataToExport.filter(d => d.statut === 'contacte').length;
  const traite    = dataToExport.filter(d => d.statut === 'traite').length;
  const annule    = dataToExport.filter(d => d.statut === 'annule').length;

  const statsRows = [
    { 'Indicateur': 'Total demandes',  'Valeur': dataToExport.length },
    { 'Indicateur': 'En attente',      'Valeur': enAttente },
    { 'Indicateur': 'Contacté',        'Valeur': contacte },
    { 'Indicateur': 'Traité',          'Valeur': traite },
    { 'Indicateur': 'Annulé',          'Valeur': annule },
    { 'Indicateur': 'Exporté le',      'Valeur': new Date().toLocaleString('fr-FR') },
  ];

  const wsStats = XLSX.utils.json_to_sheet(statsRows);
  wsStats['!cols'] = [{ wch: 20 }, { wch: 20 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Demandes');
  XLSX.utils.book_append_sheet(wb, wsStats, 'Statistiques');

  const fileName = `ASIFC_Demandes_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(wb, fileName);
};

  // Export PDF
  const exportToPDF = (exportAll = false) => {
    const dataToExport = exportAll 
      ? demandesFiltrees 
      : demandesFiltrees.filter(d => selectedDemandes.includes(d._id));

    if (dataToExport.length === 0) {
      alert('Veuillez sélectionner au moins une demande à exporter');
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4'); // Paysage pour plus de colonnes

    // En-tête
    doc.setFillColor(44, 95, 45);
    doc.rect(0, 0, 297, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('ASIFC - Permanence Numérique', 148.5, 18, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Liste des demandes d'accompagnement`, 148.5, 28, { align: 'center' });
    doc.text(`Exporté le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 148.5, 35, { align: 'center' });

    // Statistiques
    doc.setTextColor(44, 95, 45);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: ${dataToExport.length} demande(s)`, 14, 50);
    
    const enAttente = dataToExport.filter(d => d.statut === 'en_attente').length;
    const contacte = dataToExport.filter(d => d.statut === 'contacte').length;
    const traite = dataToExport.filter(d => d.statut === 'traite').length;
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`En attente: ${enAttente} | Contacté: ${contacte} | Traité: ${traite}`, 14, 56);

    // Tableau
    const tableData = dataToExport.map(d => [
      new Date(d.createdAt).toLocaleDateString('fr-FR'),
      `${d.prenom} ${d.nom}`,
      d.telephone,
      d.email || '-',
      d.adresse || '-',
      besoinLabels[d.besoin] || d.besoin,
      d.creneauHoraire || '-',
      statutLabels[d.statut] || d.statut,
    ]);

    autoTable(doc, {
      startY: 62,
      head: [['Date', 'Nom', 'Téléphone', 'Email', 'Adresse', 'Démarche', 'Créneau', 'Statut']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [44, 95, 45],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [50, 50, 50],
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250],
      },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 35 },
        2: { cellWidth: 28 },
        3: { cellWidth: 45 },
        4: { cellWidth: 45 },
        5: { cellWidth: 40 },
        6: { cellWidth: 20 },
        7: { cellWidth: 25 },
      },
      margin: { left: 14, right: 14 },
      didDrawPage: function(data) {
        // Pied de page
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `ASIFC - Association de Solidarité Internationale France-Comores | Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
          148.5,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      },
    });

    // Télécharger
    const fileName = `ASIFC_Demandes_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  // Export PDF détaillé (une page par personne)
  const exportDetailedPDF = () => {
    const dataToExport = selectedDemandes.length > 0
      ? demandesFiltrees.filter(d => selectedDemandes.includes(d._id))
      : demandesFiltrees;

    if (dataToExport.length === 0) {
      alert('Aucune demande à exporter');
      return;
    }

    const doc = new jsPDF('p', 'mm', 'a4');

    dataToExport.forEach((demande, index) => {
      if (index > 0) {
        doc.addPage();
      }

      // En-tête
      doc.setFillColor(44, 95, 45);
      doc.rect(0, 0, 210, 35, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('ASIFC - Fiche de demande', 105, 15, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Permanence Numérique`, 105, 23, { align: 'center' });
      doc.text(`N° ${index + 1}/${dataToExport.length}`, 105, 30, { align: 'center' });

      let y = 50;

      // Informations personnelles
      doc.setFillColor(230, 244, 234);
      doc.rect(14, y - 5, 182, 10, 'F');
      doc.setTextColor(44, 95, 45);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Informations personnelles', 20, y + 2);
      y += 15;

      doc.setTextColor(50, 50, 50);
      doc.setFontSize(11);
      
      const addField = (label, value) => {
        doc.setFont('helvetica', 'bold');
        doc.text(`${label}:`, 20, y);
        doc.setFont('helvetica', 'normal');
        doc.text(value || '-', 70, y);
        y += 8;
      };

      addField('Nom', `${demande.prenom} ${demande.nom}`);
      addField('Téléphone', demande.telephone);
      addField('Email', demande.email || 'Non fourni');
      addField('Adresse', demande.adresse);
      addField('Concerne', demande.concernePour === 'moi-meme' ? 'Lui-même' : 'Quelqu\'un d\'autre');

      y += 10;

      // Demande
      doc.setFillColor(255, 244, 230);
      doc.rect(14, y - 5, 182, 10, 'F');
      doc.setTextColor(255, 107, 53);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Détails de la demande', 20, y + 2);
      y += 15;

      doc.setTextColor(50, 50, 50);
      doc.setFontSize(11);

      addField('Type de démarche', besoinLabels[demande.besoin] || demande.besoin);
      addField('Créneau horaire', demande.creneauHoraire || 'Non spécifié');
      addField('Date de demande', new Date(demande.createdAt).toLocaleDateString('fr-FR'));
      addField('Statut', statutLabels[demande.statut] || demande.statut);

      if (demande.commentaire) {
        y += 5;
        doc.setFont('helvetica', 'bold');
        doc.text('Commentaire:', 20, y);
        y += 8;
        doc.setFont('helvetica', 'normal');
        
        // Gérer le texte long
        const splitComment = doc.splitTextToSize(demande.commentaire, 170);
        doc.text(splitComment, 20, y);
        y += splitComment.length * 6;
      }

      y += 15;

      // Zone de notes
      doc.setFillColor(245, 247, 250);
      doc.rect(14, y - 5, 182, 50, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.rect(14, y - 5, 182, 50, 'S');
      
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Notes / Suivi:', 20, y + 2);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.text('(Zone réservée aux annotations)', 20, y + 10);

      // Pied de page
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `ASIFC - Document confidentiel | Imprimé le ${new Date().toLocaleDateString('fr-FR')}`,
        105,
        285,
        { align: 'center' }
      );
    });

    const fileName = `ASIFC_Fiches_Detaillees_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  const besoinLabels = {
    'titre-sejour-premiere': 'Première demande titre séjour',
    'titre-sejour-renouvellement': 'Renouvellement titre séjour',
    'nationalite-francaise': 'Nationalité française',
    'changement-statut': 'Changement de statut',
    'caf-emploi-retraite': 'CAF/France Travail/Retraite',
    'etudiant': 'Étudiant',
    'caf': 'CAF',
    'cpam': 'CPAM',
    'impots': 'Impôts',
    'pole_emploi': 'Pôle Emploi',
    'retraite': 'Retraite',
    'autre': 'Autre',
  };

  const statutLabels = {
    'en_attente': 'En attente',
    'contacte': 'Contacté',
    'traite': 'Traité',
    'annule': 'Annulé',
  };

  const demandesFiltrees = demandes.filter(d => {
    const matchStatut = filtreStatut === 'tous' || d.statut === filtreStatut;
    const matchBesoin = filtreBesoin === 'tous' || d.besoin === filtreBesoin;
    return matchStatut && matchBesoin;
  });

  const sujetLabels = {
    information: "Information",
    benevole: 'Bénévolat',
    partenariat: 'Partenariat',
    permanence: 'Permanence',
    don: 'Don',
    autre: 'Autre',
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="admin-layout">
      {/* Sidebar Navigation */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <h2>🇰🇲 ASIFC Admin</h2>
        </div>
        
        <nav className="sidebar-nav">
          <button 
            className={`nav-item ${activeSection === 'demandes' ? 'active' : ''}`}
            onClick={() => setActiveSection('demandes')}
          >
            <span className="nav-icon">📋</span>
            <span className="nav-text">Demandes</span>
            <span className="nav-badge">{demandes.length}</span>
          </button>

          <button 
            className={`nav-item ${activeSection === 'contacts' ? 'active' : ''}`}
            onClick={() => setActiveSection('contacts')}
          >
            <span className="nav-icon">📬</span>
            <span className="nav-text">Messages</span>
            <span className="nav-badge">{contacts.length}</span>
          </button>

          <button 
            className="nav-item"
            onClick={() => navigate('/admin/permanences')}
          >
            <span className="nav-icon">📅</span>
            <span className="nav-text">Permanences</span>
          </button>

          <button 
            className="nav-item"
            onClick={() => navigate('/admin/actualites')}
          >
            <span className="nav-icon">📰</span>
            <span className="nav-text">Actualités</span>
          </button>
        </nav>

        <div className="sidebar-footer">
            <button 
            className="nav-item"
            onClick={() => navigate('/')}
          >
            <span className="nav-text">Retourner au site</span>
          </button>
          <button 
            className="nav-item"
            onClick={() => navigate('/admin/register')}
          >
            <span className="nav-icon">+</span>
            <span className="nav-text">Nouvel Admin</span>
          </button>

          <button className="nav-item logout" onClick={handleLogout}>
            <span className="nav-icon">🚪</span>
            <span className="nav-text">Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-header">
          <h1>
            {activeSection === 'demandes' ? '📊 Dashboard - Demandes' : '📬 Dashboard - Messages de Contact'}
          </h1>
        </div>

        <div className="admin-content">
          {/* Section Demandes */}
          {activeSection === 'demandes' && (
            <div className="dashboard-section">
              <div className="list-header">
                <h2>Liste des demandes ({demandesFiltrees.length})</h2>
                <div className="header-actions">
                  <div className="filtres">
                    <select 
                      value={filtreStatut} 
                      onChange={(e) => setFiltreStatut(e.target.value)}
                      className="filtre-select"
                    >
                      <option value="tous">Tous les statuts</option>
                      <option value="en_attente">En attente</option>
                      <option value="contacte">Contacté</option>
                      <option value="traite">Traité</option>
                      <option value="annule">Annulé</option>
                    </select>

                    <select 
                      value={filtreBesoin} 
                      onChange={(e) => setFiltreBesoin(e.target.value)}
                      className="filtre-select"
                    >
                      <option value="tous">Toutes les démarches</option>
                      <option value="titre-sejour-premiere">Première demande titre séjour</option>
                      <option value="titre-sejour-renouvellement">Renouvellement titre séjour</option>
                      <option value="nationalite-francaise">Nationalité française</option>
                      <option value="changement-statut">Changement de statut</option>
                      <option value="caf-emploi-retraite">CAF/France Travail/Retraite</option>
                      <option value="etudiant">Étudiant</option>
                    </select>
                  </div>

                  {/* Boutons d'export */}
                  <div className="export-buttons">
                    <button 
                      className="btn-export"
                      onClick={() => exportToPDF(true)}
                      title="Exporter toutes les demandes filtrées"
                    >
                      📄 Exporter tout ({demandesFiltrees.length})
                    </button>
                    <button 
                      className="btn-export btn-export-selected"
                      onClick={() => exportToPDF(false)}
                      disabled={selectedDemandes.length === 0}
                      title="Exporter les demandes sélectionnées"
                    >
                      📄 Exporter sélection ({selectedDemandes.length})
                    </button>

                    <button
                      className="btn-export btn-export-excel"
                      onClick={() => exportToExcel(true)}
                      title="Exporter toutes les demandes filtrées en Excel"
                    >
                      📊 Excel tout ({demandesFiltrees.length})
                    </button>
                    <button
                      className="btn-export btn-export-excel"
                      onClick={() => exportToExcel(false)}
                      disabled={selectedDemandes.length === 0}
                      title="Exporter la sélection en Excel"
                    >
                      📊 Excel sélection ({selectedDemandes.length})
                    </button>

                    <button 
                      className="btn-export btn-export-detailed"
                      onClick={exportDetailedPDF}
                      title="Exporter en fiches détaillées"
                    >
                      📑 Fiches détaillées
                    </button>
                  </div>
                </div>
              </div>

              <table className="demandes-table">
                <thead>
                  <tr>
                    <th>
                      <input 
                        type="checkbox"
                        checked={selectedDemandes.length === demandesFiltrees.length && demandesFiltrees.length > 0}
                        onChange={handleSelectAll}
                        title="Tout sélectionner"
                      />
                    </th>
                    <th>Date</th>
                    <th>Nom</th>
                    <th>Téléphone</th>
                    <th>Email</th>
                    <th>Démarche</th>
                    <th>Créneau</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {demandesFiltrees.map((d) => (
                    <tr key={d._id} className={selectedDemandes.includes(d._id) ? 'selected-row' : ''}>
                      <td>
                        <input 
                          type="checkbox"
                          checked={selectedDemandes.includes(d._id)}
                          onChange={() => handleSelectDemande(d._id)}
                        />
                      </td>
                      <td>{new Date(d.createdAt).toLocaleDateString('fr-FR')}</td>
                      <td>{d.prenom} {d.nom}</td>
                      <td>
                        <a href={`tel:${d.telephone}`} className="phone-link">{d.telephone}</a>
                      </td>
                      <td>
                        {d.email ? (
                          <a href={`mailto:${d.email}`} className="email-link">{d.email}</a>
                        ) : (
                          <span className="no-email">Non fourni</span>
                        )}
                      </td>
                      <td>{besoinLabels[d.besoin] || d.besoin}</td>
                      <td>{d.creneauHoraire || '-'}</td>
                      <td>
                        <select
                          value={d.statut}
                          onChange={(e) => handleStatusChange(d._id, e.target.value)}
                          className={`statut-select ${d.statut}`}
                        >
                          <option value="en_attente">En attente</option>
                          <option value="contacte">Contacté</option>
                          <option value="traite">Traité</option>
                          <option value="annule">Annulé</option>
                        </select>
                      </td>
                      <td>
                        <button onClick={() => handleDelete(d._id)} className="delete-btn">
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {demandesFiltrees.length === 0 && (
                <p style={{textAlign: 'center', padding: '20px', color: '#718096'}}>
                  Aucune demande pour ce filtre
                </p>
              )}
            </div>
          )}

          {/* Section Messages de Contact */}
          {activeSection === 'contacts' && (
            <div className="dashboard-section">
              <div className="list-header">
                <h2>Messages de Contact ({contacts.filter(c => filtreContactStatut === 'tous' || c.statut === filtreContactStatut).length})</h2>
                <div className="filtres">
                  <select 
                    value={filtreContactStatut} 
                    onChange={(e) => setFiltreContactStatut(e.target.value)}
                    className="filtre-select"
                  >
                    <option value="tous">Tous les statuts</option>
                    <option value="nouveau">Nouveau</option>
                    <option value="lu">Lu</option>
                    <option value="en_cours">En cours</option>
                    <option value="traite">Traité</option>
                  </select>
                </div>
              </div>

              <table className="demandes-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Sujet</th>
                    <th>Message</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts
                    .filter(c => filtreContactStatut === 'tous' || c.statut === filtreContactStatut)
                    .map((c) => (
                      <tr key={c._id}>
                        <td>{new Date(c.createdAt).toLocaleDateString('fr-FR')}</td>
                        <td>{c.prenom} {c.nom}</td>
                        <td>
                          <a href={`mailto:${c.email}`} className="email-link">{c.email}</a>
                        </td>
                        <td>
                          {c.telephone ? (
                            <a href={`tel:${c.telephone}`} className="phone-link">{c.telephone}</a>
                          ) : (
                            <span className="no-email">Non fourni</span>
                          )}
                        </td>
                        <td>{sujetLabels[c.sujet]}</td>
                        <td>
                          <div style={{maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                            {c.message}
                          </div>
                        </td>
                        <td>
                          <select
                            value={c.statut}
                            onChange={(e) => handleContactStatusChange(c._id, e.target.value)}
                            className={`statut-select ${c.statut}`}
                          >
                            <option value="nouveau">Nouveau</option>
                            <option value="lu">Lu</option>
                            <option value="en_cours">En cours</option>
                            <option value="traite">Traité</option>
                          </select>
                        </td>
                        <td>
                          <button onClick={() => handleContactDelete(c._id)} className="delete-btn">
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {contacts.filter(c => filtreContactStatut === 'tous' || c.statut === filtreContactStatut).length === 0 && (
                <p style={{textAlign: 'center', padding: '20px', color: '#718096'}}>
                  Aucun message de contact pour ce filtre
                </p>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminDashboard;